import axios from 'axios';
import { OHLCBlock } from '../types/market.types';
import { TickerSnapData } from '../types/symbol.types';
import { API_ENDPOINTS } from '../utils/constants';

export interface WebSocketMessage {
  topic: string;
  type: string;
  subject: string;
  data: any;
}

export interface TickerData {
  symbol: string;
  bestAsk: string;
  bestAskSize: string;
  bestBid: string;
  bestBidSize: string;
  price: string;
  sequence: string;
  size: string;
  time: number;
}

export interface OrderBookEntry {
  price: string;
  size: string;
}

export interface OrderBookData {
  symbol: string;
  asks: OrderBookEntry[];
  bids: OrderBookEntry[];
  timestamp: number;
  sequence: string;
}

export interface PublicTokenResponse {
  code: string;
  data: {
    token: string;
    instanceServers: {
      endpoint: string;
      encrypt: boolean;
      protocol: string;
      pingInterval: number;
      pingTimeout: number;
    }[];
  };
}

export class TradingDataService {
  // Its /kucoin is proxied to https://api.kucoin.com
  // private baseUrl = '/kucoin'; // in cors error using proxy
  // private baseUrl = 'https://api.kucoin.com';
  // private baseUrl = 'http://localhost:8080';

  private baseUrl = import.meta.env.VITE_PROXY_API_BASE_URL;
  private kucoinUrlV1 = `${this.baseUrl}api/v1/market`;
  private wsUrlSpot = 'wss://ws-api-spot.kucoin.com';
  private webSocketConnections = new Map<string, WebSocket[]>();

  // Cache for storing the public token
  private static cachedToken: string | null = null;
  private static tokenPromise: Promise<string | null> | null = null;
  private static tokenExpiry: number = 0;
  private static readonly TOKEN_VALIDITY_DURATION = 23 * 60 * 60 * 1000; // 23 hours in milliseconds

  async getCandleData(
    symbol: string = 'BTC-USDT',
    interval: string = '1min',
    from?: number,
    to?: number
  ): Promise<OHLCBlock[]> {
    const daysAgo = 1 / 5;
    const endTime = to || Math.floor(Date.now() / 1000);
    const startTime = from || endTime - Math.trunc(60 * 60 * 24 * daysAgo);
    // + Math.trunc((Math.random() * 1000));

    const url = `${this.kucoinUrlV1}/candles`;
    try {
      const response = await axios.get(url, {
        params: {
          symbol,
          type: interval,
          startAt: startTime,
          endAt: endTime,
        },
      });
      const tochlvVData = response.data?.data || [];
      const ohlc = tochlvVData
        .map((item: string[]) => ({
          time: parseInt(item[0]) * 1000,
          open: parseFloat(item[1]),
          close: parseFloat(item[2]),
          high: parseFloat(item[3]),
          low: parseFloat(item[4]),
          volume: parseFloat(item[5]),
        }))
        .sort((a: any, b: any) => a.time - b.time) as OHLCBlock[];
      return ohlc;
    } catch (error: any) {
      throw new Error(
        `Axios error: ${error.response?.status} ${error.response?.statusText}`
      );
    }
  }

  /**
   * Disconnect WebSocket connections for a given symbol
   * @param symbol Symbol to disconnect (e.g., 'BTC-USDT')
   * @param fallback Optional fallback function to call after disconnection
   */
  disconnectWebSocket(symbol: string, fallback?: () => void): void {
    const connections = this.webSocketConnections.get(symbol);
    if (connections && connections.length > 0) {
      connections.forEach(ws => {
        ws.close(1000, 'Normal closure');
      });
      this.webSocketConnections.delete(symbol);

      // Call the optional fallback function if provided
      if (fallback) {
        fallback();
      }
    } else {
      console.log(`No WebSocket connections found for symbol: ${symbol}`);
    }
  }

  /**
   * Disconnect all WebSocket connections except for a given symbol
   * @param keepSymbol Symbol to keep connected (e.g., 'BTC-USDT')
   * @param fallback Optional fallback function to call after disconnection
   */
  disconnectAllWebSocketsExcept(keepSymbol: string, fallback?: () => void): void {
    let disconnectedCount = 0;

    for (const [symbol, connections] of this.webSocketConnections.entries()) {
      if (symbol !== keepSymbol && connections.length > 0) {
        connections.forEach(ws => {
          console.log("Closing WS for", symbol, ws);
          ws.close(1000, 'Normal closure');
          console.log(`Disconnected WebSocket connections for symbol: ${symbol}`);
        });
        this.webSocketConnections.delete(symbol);
        disconnectedCount++;
      }
    }

    console.log(`Disconnected ${disconnectedCount} WebSocket connection groups, kept connections for: ${keepSymbol}`);

    // Call the optional fallback function if provided
    if (fallback) {
      fallback();
    }
  }

  connectWebSocket(
    symbol: string,
    onMessage: (data: OHLCBlock) => void,
    interval: string = '1min'
  ): WebSocket {
    // Create WebSocket connection with proper KuCoin format
    const ws = new WebSocket(this.wsUrlSpot);

    let authenticatedWs: WebSocket | null = null;
    let pingInterval: NodeJS.Timeout | null = null;

    ws.onopen = async () => {
      // console.log('KuCoin WebSocket initial connection established');

      try {
        // Get public token for WebSocket connection
        const tokenData = await this.getPublicToken();
        if (tokenData) {
          // Close initial connection and connect with token
          ws.close();

          authenticatedWs = new WebSocket(tokenData);

          authenticatedWs.onopen = () => {
                // Store the WebSocket connection
            if (!this.webSocketConnections.has(symbol)) {
              this.webSocketConnections.set(symbol, []);
            }
            this.webSocketConnections.get(symbol)!.push(authenticatedWs!);
            // console.log('KuCoin WebSocket authenticated');

            // Subscribe to kline updates
            const subscribeMessage = {
              id: Date.now(),
              type: 'subscribe',
              topic: `/market/candles:${symbol}_${this.mapIntervalToKuCoinFormat(
                interval
              )}`,
              response: true,
              privateChannel: false,
            };

            authenticatedWs?.send(JSON.stringify(subscribeMessage));
            // console.log('Subscribed to kline updates:', symbol, interval);

            // Set up ping interval to keep connection alive
            // KuCoin recommends pingInterval of 18000ms (18 seconds)
            pingInterval = setInterval(() => {
              if (
                authenticatedWs &&
                authenticatedWs.readyState === WebSocket.OPEN
              ) {
                authenticatedWs.send(
                  JSON.stringify({
                    id: Date.now(),
                    type: 'ping',
                  })
                );
                // console.log('Sent ping to KuCoin WebSocket');
              }
            }, 18000);
          };

          authenticatedWs.onmessage = (event) => {
            try {
              const data = JSON.parse(event.data) as WebSocketMessage;
              // console.log('KuCoin WebSocket message received:', data);

              if (data.type === 'pong') {
                // console.log('Received pong from KuCoin WebSocket');
                return;
              }

              if (
                data.type === 'message' &&
                data.subject === 'trade.candles.update' &&
                data.data
              ) {
                // Convert KuCoin format to expected format
                const tochlvVData = data.data.candles;
                const candleData = {
                  time: parseInt(tochlvVData[0]) * 1000,
                  open: parseFloat(tochlvVData[1]),
                  close: parseFloat(tochlvVData[2]),
                  high: parseFloat(tochlvVData[3]),
                  low: parseFloat(tochlvVData[4]),
                  volume: parseFloat(tochlvVData[5]),
                };
                onMessage(candleData);
              } else if (data.type === 'welcome') {
                // console.log('KuCoin WebSocket welcome received');
              } else if (data.type === 'ack') {
                // console.log('KuCoin subscription acknowledged:', data);
              }
            } catch (error) {
              console.error('Error parsing WebSocket message:', error);
            }
          };

          authenticatedWs.onerror = (error) => {
            console.error('KuCoin WebSocket error:', error);
          };

          authenticatedWs.onclose = () => {
            console.log('KuCoin WebSocket connection closed', symbol);
            // Clean up ping interval when connection closes
            if (pingInterval) {
              clearInterval(pingInterval);
              pingInterval = null;
            }
            // Remove from connections Map when actually closed
            if (authenticatedWs) {
              this.removeWebSocketFromMap(symbol, authenticatedWs);
            }
          };
        }
      } catch (error) {
        console.error('Failed to authenticate WebSocket:', error);
      }
    };

    ws.onerror = (error) => {
      console.error('Initial WebSocket connection error:', error);
    };

    ws.onclose = () => {
      // alert(`Initial WebSocket connection closed for symbol: ${symbol}`);
    };

    // Return the authenticated WebSocket or the initial one
    const finalWs = authenticatedWs || ws;

    // Add cleanup method to the WebSocket
    const originalClose = finalWs.close.bind(finalWs);
    finalWs.close = (code?: number, reason?: string) => {
      if (pingInterval) {
        clearInterval(pingInterval);
        pingInterval = null;
      }
      originalClose(code, reason);
    };

    return finalWs;
  }

  private async getPublicToken(): Promise<string | null> {
    // Check if we have a valid cached token
    const now = Date.now();
    // if (TradingDataService.cachedToken && now < TradingDataService.tokenExpiry) {
    //   console.log('Using cached public token');
    //   return TradingDataService.cachedToken;
    // }

    // // If there's already a pending token request, return it
    // if (TradingDataService.tokenPromise) {
    //   console.log('Waiting for existing token request');
    //   return TradingDataService.tokenPromise;
    // }

    // Create a new token request
    TradingDataService.tokenPromise = this.fetchNewToken();

    try {
      const token = await TradingDataService.tokenPromise;
      if (token) {
        // Cache the token and set expiry
        TradingDataService.cachedToken = token;
        TradingDataService.tokenExpiry =
          now + TradingDataService.TOKEN_VALIDITY_DURATION;
        // console.log('New public token cached, expires in 23 hours');
      }
      return token;
    } finally {
      // Clear the pending promise
      TradingDataService.tokenPromise = null;
    }
  }

  private async fetchNewToken(): Promise<string | null> {
    try {
      // console.log('Fetching new public token from KuCoin API');
      // done update this url
      const response = await axios.post<PublicTokenResponse>(
        `${API_ENDPOINTS.proxy}api/v1/bullet-public`
      );
      if (response.data && response.data.data) {
        const { token, instanceServers } = response.data.data;
        if (instanceServers && instanceServers.length > 0) {
          const server = instanceServers[0];
          return `${server.endpoint}?token=${token}`;
        }
      }
      return null;
    } catch (error) {
      console.error('Error getting public token:', error);
      return null;
    }
  }

  /**
   * Clear the cached token (useful for testing or when token is known to be invalid)
   */
  static clearTokenCache(): void {
    TradingDataService.cachedToken = null;
    TradingDataService.tokenPromise = null;
    TradingDataService.tokenExpiry = 0;
    console.log('Public token cache cleared');
  }

  /**
   * Remove a specific WebSocket from the connections map
   * @param symbol Symbol to remove from
   * @param ws WebSocket instance to remove
   */
  private removeWebSocketFromMap(symbol: string, ws: WebSocket): void {
    const connections = this.webSocketConnections.get(symbol);
    if (connections) {
      const index = connections.indexOf(ws);
      if (index > -1) {
        connections.splice(index, 1);
        // If no more connections for this symbol, remove the symbol entirely
        if (connections.length === 0) {
          this.webSocketConnections.delete(symbol);
        }
        console.log(`Removed WebSocket from Map for symbol: ${symbol}`);
      }
    }
  }

  // Helper method to map interval to KuCoin format
  private mapIntervalToKuCoinFormat(interval: string): string {
    const intervalMap: { [key: string]: string; } = {
      '1min': '1min',
      '3min': '3min',
      '5min': '5min',
      '15min': '15min',
      '30min': '30min',
      '1hour': '1hour',
      '2hour': '2hour',
      '4hour': '4hour',
      '6hour': '6hour',
      '8hour': '8hour',
      '12hour': '12hour',
      '1day': '1day',
      '1week': '1week',
    };

    return intervalMap[interval] || '1hour';
  }

  async getSymbols(): Promise<string[]> {
    const response = await fetch(`${this.kucoinUrlV1}/symbols`);
    const data = await response.json();
    return data.data?.map((item: any) => item.symbol) || [];
  }

  /**
   * Connect to WebSocket for specific symbol ticker data using /market/ticker:{symbol} format
   * @param symbols Array of symbols to subscribe to (e.g., ['BTC-USDT'])
   * @param onTickerUpdate Callback function to handle ticker updates
   * @returns WebSocket connection
   */
  connectSpecificTickerWebSocket(
    onTickerUpdate: (ticker: TickerData) => void,
    symbols: string[]
  ): WebSocket {
    console.log('symbols', symbols);
    // Create WebSocket connection with proper KuCoin format
    const ws = new WebSocket(this.wsUrlSpot);

    let authenticatedWs: WebSocket | null = null;
    let pingInterval: NodeJS.Timeout | null = null;

    ws.onopen = async () => {
      console.log(
        'KuCoin Specific Ticker WebSocket initial connection established for',
        symbols
      );

      try {
        // Get public token for WebSocket connection
        const tokenData = await this.getPublicToken();
        if (tokenData) {
          // Close initial connection and connect with token
          ws.close();

          authenticatedWs = new WebSocket(tokenData);

          authenticatedWs.onopen = () => {
            console.log('KuCoin Specific Ticker WebSocket authenticated');

            // Subscribe to ticker updates for specific symbols
            // Format: /market/ticker:{symbol},{symbol}
            const topic = `/market/ticker:${symbols.join(',')}`;
            const subscribeMessage = {
              id: Date.now(),
              type: 'subscribe',
              topic: topic,
              response: true,
            };

            authenticatedWs?.send(JSON.stringify(subscribeMessage));
            console.log(
              'Subscribed to specific ticker updates for symbols:',
              symbols
            );
            // Set up ping interval to keep connection alive
            // KuCoin recommends pingInterval of 18000ms (18 seconds)
            pingInterval = setInterval(() => {
              if (
                authenticatedWs &&
                authenticatedWs.readyState === WebSocket.OPEN
              ) {
                authenticatedWs.send(
                  JSON.stringify({
                    id: Date.now(),
                    type: 'ping',
                  })
                );
                console.log('Sent ping to KuCoin WebSocket');
              }
            }, 18000);
          };

          authenticatedWs.onmessage = (event) => {
            try {
              const data = JSON.parse(event.data) as WebSocketMessage;
              // console.log('KuCoin Specific Ticker WebSocket message received:', data);

              if (data.type === 'pong') {
                console.log(
                  'Received pong from KuCoin Specific Ticker WebSocket'
                );
                return;
              }

              if (
                data.type === 'message' &&
                data.subject === 'trade.ticker' &&
                data.data
              ) {
                // Extract symbol from topic
                const topicParts = data.topic.split(':');
                const symbolFromTopic = topicParts[1];

                // Convert KuCoin ticker format to our TickerData format
                const tickerData: TickerData = {
                  symbol: symbolFromTopic,
                  bestAsk: data.data.bestAsk,
                  bestAskSize: data.data.bestAskSize,
                  bestBid: data.data.bestBid,
                  bestBidSize: data.data.bestBidSize,
                  price: data.data.price,
                  sequence: data.data.sequence,
                  size: data.data.size,
                  time: data.data.Time || data.data.time,
                };
                onTickerUpdate(tickerData);
              } else if (data.type === 'welcome') {
                console.log(
                  'KuCoin Specific Ticker WebSocket welcome received'
                );
              } else if (data.type === 'ack') {
                console.log(
                  'KuCoin specific ticker subscription acknowledged:',
                  data
                );
              }
            } catch (error) {
              console.error(
                'Error parsing specific ticker WebSocket message:',
                error
              );
            }
          };

          authenticatedWs.onerror = (error) => {
            console.error('KuCoin Specific Ticker WebSocket error:', error);
          };

          authenticatedWs.onclose = () => {
            console.log('KuCoin Specific Ticker WebSocket connection closed');
            // Clean up ping interval when connection closes
            if (pingInterval) {
              clearInterval(pingInterval);
              pingInterval = null;
            }
          };
        }
      } catch (error) {
        console.error(
          'Failed to authenticate specific ticker WebSocket:',
          error
        );
      }
    };

    ws.onerror = (error) => {
      console.error(
        'Initial Specific Ticker WebSocket connection error:',
        error
      );
    };

    ws.onclose = () => {
      console.log('Initial Specific Ticker WebSocket connection closed');
    };

    // Return the authenticated WebSocket or the initial one
    const finalWs = authenticatedWs || ws;

    // Add cleanup method to the WebSocket
    const originalClose = finalWs.close.bind(finalWs);
    finalWs.close = (code?: number, reason?: string) => {
      if (pingInterval) {
        clearInterval(pingInterval);
        pingInterval = null;
      }
      originalClose(code, reason);
    };

    return finalWs;
  }

  /**
   * Connect to WebSocket for single symbol ticker data
   * @param symbol Single symbol to subscribe to (e.g., 'BTC-USDT')
   * @param onTickerUpdate Callback function to handle ticker updates
   * @returns WebSocket connection
   */
  connectSingleTickerWebSocket(
    symbol: string,
    onTickerUpdate: (ticker: TickerData) => void
  ): WebSocket {
    // @ts-ignore
    return this.connectSpecificTickerWebSocket([symbol], onTickerUpdate);
  }

  /**
   * Fetch order book data from REST API (Level 2 - 100 best ask/bid orders)
   * @param symbol Symbol to fetch (e.g., 'BTC-USDT')
   * @returns Promise<OrderBookData>
   */
  async getOrderBook(symbol: string): Promise<OrderBookData | undefined> {
    // const url = `${this.kucoinUrlV1}/orderbook/level2_100`;
    const url = `${API_ENDPOINTS.proxy}api/v1/market/orderbook/level2_20`;
    try {
      const response = await axios.get(url, {
        params: { symbol },
      });
      const apiData = response.data?.data;
      if (apiData) {
        const orderBookData: OrderBookData = {
          symbol: symbol,
          asks:
            apiData.asks?.map((ask: string[]) => ({
              price: ask[0],
              size: ask[1],
            })) || [],
          bids:
            apiData.bids?.map((bid: string[]) => ({
              price: bid[0],
              size: bid[1],
            })) || [],
          timestamp: apiData.time || Date.now(),
          sequence: apiData.sequence || '',
        };
        return orderBookData;
      }
    } catch (error: any) {
      // console.log(
      //   `Failed to fetch order book: ${error.response?.status} ${error.response?.statusText}`
      // );
    }
  }

  /**
   * Connect to WebSocket for order book data (Level 2 - 50 best ask/bid orders)
   * @param symbol Symbol to subscribe to (e.g., 'BTC-USDT')
   * @param onOrderBookUpdate Callback function to handle order book updates
   * @returns WebSocket connection
   */
  connectOrderBookWebSocket(
    symbol: string,
    onOrderBookUpdate: (orderBook: OrderBookData) => void
  ): WebSocket {
    // Create WebSocket connection with proper KuCoin format
    const ws = new WebSocket(this.wsUrlSpot);

    let authenticatedWs: WebSocket | null = null;
    let pingInterval: NodeJS.Timeout | null = null;

    ws.onopen = async () => {
      // console.log('KuCoin Order Book WebSocket initial connection established');

      try {
        // Get public token for WebSocket connection
        const tokenData = await this.getPublicToken();
        if (tokenData) {
          // Close initial connection and connect with token
          ws.close();

          authenticatedWs = new WebSocket(tokenData);

          // Store the WebSocket connection immediately after creation
          if (!this.webSocketConnections.has(symbol)) {
            this.webSocketConnections.set(symbol, []);
          }
          this.webSocketConnections.get(symbol)!.push(authenticatedWs!);

          authenticatedWs.onopen = () => {
            // console.log('KuCoin Order Book WebSocket authenticated');

            // Subscribe to order book updates (Level 2 - 50 best orders)
            const subscribeMessage = {
              id: Date.now(),
              type: 'subscribe',
              topic: `/spotMarket/level2Depth50:${symbol}`,
              response: true,
            };

            authenticatedWs?.send(JSON.stringify(subscribeMessage));
            // console.log('Subscribed to order book updates for symbol:', symbol);

            // Set up ping interval to keep connection alive
            // KuCoin recommends pingInterval of 18000ms (18 seconds)
            pingInterval = setInterval(() => {
              if (
                authenticatedWs &&
                authenticatedWs.readyState === WebSocket.OPEN
              ) {
                authenticatedWs.send(
                  JSON.stringify({
                    id: Date.now(),
                    type: 'ping',
                  })
                );
                // console.log('Sent ping to KuCoin Order Book WebSocket');
              }
            }, 18_000);
          };

          authenticatedWs.onmessage = (event) => {
            try {
              const data = JSON.parse(event.data) as WebSocketMessage;
              // console.log(
              //   'KuCoin Order Book WebSocket message received:',
              //   data
              // );

              if (data.type === 'pong') {
                // console.log('Received pong from KuCoin Order Book WebSocket');
                return;
              }

              if (
                data.type === 'message' &&
                data.subject === 'level2' &&
                data.data
              ) {
                // Convert KuCoin order book format to our OrderBookData format
                const orderBookData: OrderBookData = {
                  symbol: symbol,
                  asks:
                    data.data.asks?.map((ask: string[]) => ({
                      price: ask[0],
                      size: ask[1],
                    })) || [],
                  bids:
                    data.data.bids?.map((bid: string[]) => ({
                      price: bid[0],
                      size: bid[1],
                    })) || [],
                  timestamp: data.data.timestamp || Date.now(),
                  sequence:
                    data.data.sequenceStart || data.data.sequenceEnd || '',
                };
                onOrderBookUpdate(orderBookData);
              } else if (data.type === 'welcome') {
                // console.log('KuCoin Order Book WebSocket welcome received');
              } else if (data.type === 'ack') {
                // console.log(
                //   'KuCoin order book subscription acknowledged:',
                //   data
                // );
              }
            } catch (error) {
              console.error(
                'Error parsing order book WebSocket message:',
                error
              );
            }
          };

          authenticatedWs.onerror = (error) => {
            console.error('KuCoin Order Book WebSocket error:', error);
          };

          authenticatedWs.onclose = () => {
            console.log('KuCoin Order Book WebSocket connection closed');
            // Clean up ping interval when connection closes
            if (pingInterval) {
              clearInterval(pingInterval);
              pingInterval = null;
            }
            // Remove from connections Map when actually closed
            if (authenticatedWs) {
              this.removeWebSocketFromMap(symbol, authenticatedWs);
            }
          };
        }
      } catch (error) {
        console.error('Failed to authenticate order book WebSocket:', error);
      }
    };

    ws.onerror = (error) => {
      console.error('Initial Order Book WebSocket connection error:', error);
    };

    ws.onclose = () => {
      // console.log('Initial Order Book WebSocket connection closed');
    };

    // Return the authenticated WebSocket or the initial one
    const finalWs = authenticatedWs || ws;

    // Add cleanup method to the WebSocket
    const originalClose = finalWs.close.bind(finalWs);
    finalWs.close = (code?: number, reason?: string) => {
      if (pingInterval) {
        clearInterval(pingInterval);
        pingInterval = null;
      }
      originalClose(code, reason);
    };

    return finalWs;
  }

  async connectTickerSnapWebSocket(
    symbol: string,
    onTickerUpdate: (data: TickerSnapData) => void
  ): Promise<WebSocket | null> {
    const tokenData = await this.getPublicToken();
    if (!tokenData) {
      console.error('Failed to get public token');
      return null;
    }

    const ws = new WebSocket(tokenData);
    let pingInterval: NodeJS.Timeout | null = null;

    ws.onopen = () => {
      // Subscribe to ticker updates
      const subscribeMsg = {
        id: Date.now(),
        type: 'subscribe',
        topic: `/market/snapshot:${symbol}`,
        response: true,
      };
      ws.send(JSON.stringify(subscribeMsg));
      // console.log('Subscribed to snapshot for', symbol);

      // Set up ping interval to keep connection alive (18 seconds as recommended)
      pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(
            JSON.stringify({
              id: Date.now(),
              type: 'ping',
            })
          );
          // console.log('Sent ping to snapshot WebSocket');
        }
      }, 18000);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'pong') {
          // console.log('Received pong from snapshot WebSocket');
          return;
        }

        if (data.type === 'message' && data.subject === 'trade.snapshot') {
          const snapData = data?.data?.data;
          if (snapData) {
            const tickerSnapData: TickerSnapData = {
              lastTradedPrice: snapData.lastTradedPrice,
              changeRate: snapData.changeRate,
              changePrice: snapData.changePrice,
              high: snapData.high,
              low: snapData.low,
              vol: snapData.vol,           // in base currency (ETH)
              volValue: snapData.volValue, // in quote currency (USDT)
            };

            onTickerUpdate(tickerSnapData);
          }
        }
      } catch (err) {
        console.error('Error parsing snapshot WS message:', err);
      }
    };

    ws.onerror = (err) => console.error('snapshot WS error:', err);

    ws.onclose = () => {
      console.log('snapshot WS closed');
      // Clean up ping interval when connection closes
      if (pingInterval) {
        clearInterval(pingInterval);
        pingInterval = null;
      }
    };

    // Add cleanup method to the WebSocket
    const originalClose = ws.close.bind(ws);
    ws.close = (code?: number, reason?: string) => {
      if (pingInterval) {
        clearInterval(pingInterval);
        pingInterval = null;
      }
      originalClose(code, reason);
    };

    // Store the WebSocket connection for ticker snap
    if (!this.webSocketConnections.has(symbol)) {
      this.webSocketConnections.set(symbol, []);
    }
    this.webSocketConnections.get(symbol)!.push(ws);

    return ws;
  }
}
